/**
 * Dashboard Page
 *
 * Main landing page for authenticated users.
 * Allows users to:
 * - Create new PDF processing sessions
 * - View active session details
 * - Navigate to workflow page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CreditBatch
} from '../api/billing';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Container,
  AppBar,
  Toolbar,
  Grid,
  Tabs,
  Tab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { sessionsApi } from '../api/sessions';
import { customersApi } from '../api/customers';
import { onboardingApi } from '../api/onboarding';
import { useSessionStore } from '../store/sessionStore';
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useSessionPolling } from '../hooks/useSessionPolling';
import { useSessionResume } from '../hooks/useSessionResume';
import { FolderUpload } from '../components/pdf/FolderUpload';
import { billingApi } from '../api/billing';
import { useUser } from '../contexts/UserContext';

import { type SubscriptionPlan } from '../config/plans';
import PlanSelectionDialog from '../components/billing/PlanSelectionDialog';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeSession, setActiveSession, setConnectionIds } = useSessionStore();
  const { showSuccess, showError, showWarning } = useSnackbar();

  const { status: userStatus, refreshStatus } = useUser();
  const [customerId, setCustomerId] = useState('');
  const [practiceId, setPracticeId] = useState('');
  const [customerUserKey, setCustomerUserKey] = useState('');
  const [emrUsername, setEmrUsername] = useState('');
  const [emrPassword, setEmrPassword] = useState('');

  const [practices, setPractices] = useState<any[]>([]);
  const selectedPractice = practices.find(p => p.practice_id === practiceId);
  const emrConnectionId = selectedPractice?.emr_connection_id || '';

  const [tabIndex, setTabIndex] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userStatus) {
      setCustomerId(userStatus.customer_id || '');
      setCustomerUserKey(userStatus.customer_staff_key || '');
    }
  }, [userStatus]);

  const [editingPracticeId, setEditingPracticeId] = useState<string | null>(null);
  const [openPracticeDialog, setOpenPracticeDialog] = useState(false);
  const [openCredsDialog, setOpenCredsDialog] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState<string | null>(null);
  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [openLocationConfirmDialog, setOpenLocationConfirmDialog] = useState(false);
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const [organizationSettings, setOrganizationSettings] = useState({
    display_name: '',
    organization_name: '',
    organization_website: '',
    organization_support_email: '',
    legal_name: '',
    tax_id: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    timezone: '',
    phone_country_code: '',
    phone_number: '',
    billing_email: '',
    billing_channel: 'Stripe',
    organization_updated_at: '',
    is_owner_admin: false,
    is_customer_admin: false,
    is_staff: false
  });
  const [creds, setCreds] = useState({ api_url: '', api_secret_key: '' });
  const [newPractice, setNewPractice] = useState({
    global_name: '',
    external_code: '',
    api_base_url: '',
    api_customer_key: '',
    emr_vendor: 'Tebra',
    city: '',
    state: '',
  });

  // Provisioning State
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionKey, setProvisionKey] = useState('');
  const [provisionPracticeIds, setProvisionPracticeIds] = useState<string[]>([]);
  const [provisionRoles, setProvisionRoles] = useState({
    isStaff: true,
    isCustomerAdmin: false,
    isOwnerAdmin: false
  });
  const [provisionResult, setProvisionResult] = useState<any>(null);
  const [staffList, setStaffList] = useState<any[]>([]);

  const handleProvision = async () => {
    try {
      setLoading(true);
      setError('');
      setProvisionResult(null);

      const res = await customersApi.provisionStaff({
        email: provisionEmail,
        customer_staff_key: provisionKey || undefined,
        is_staff: provisionRoles.isStaff,
        is_customer_admin: provisionRoles.isCustomerAdmin,
        is_owner_admin: provisionRoles.isOwnerAdmin,
        practice_ids: provisionPracticeIds
      });
      setProvisionResult(res);
      showSuccess('Staff provisioned successfully');
      setProvisionEmail('');
      setProvisionKey('');
      setProvisionPracticeIds([]);
      setProvisionRoles({ isStaff: true, isCustomerAdmin: false, isOwnerAdmin: false });
      await fetchStaffList();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to provision staff');
    } finally {
      setLoading(false);
    }
  };

  // Edit Staff State
  const [editStaffModalOpen, setEditStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [editStaffRoles, setEditStaffRoles] = useState({ isStaff: false, isCustomerAdmin: false, isOwnerAdmin: false });
  const [editStaffPracticeIds, setEditStaffPracticeIds] = useState<string[]>([]);
  const [editStaffLoading, setEditStaffLoading] = useState(false);

  const handleOpenEditStaff = (member: any) => {
    setEditingStaff(member);
    setEditStaffRoles({
      isStaff: member.is_staff || false,
      isCustomerAdmin: member.is_customer_admin || false,
      isOwnerAdmin: member.is_owner_admin || false
    });
    setEditStaffPracticeIds(member.practice_ids || []);
    setEditStaffModalOpen(true);
  };

  const handleSaveStaffEdits = async () => {
    if (!editingStaff) return;
    try {
      setEditStaffLoading(true);
      await customersApi.updateStaffAssignments(editingStaff.email, {
        is_staff: editStaffRoles.isStaff,
        is_customer_admin: editStaffRoles.isCustomerAdmin,
        is_owner_admin: editStaffRoles.isOwnerAdmin,
        practice_ids: editStaffPracticeIds
      });
      showSuccess(`Updated assignments for ${editingStaff.email}`);
      setEditStaffModalOpen(false);
      await fetchStaffList();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to update staff assignments');
    } finally {
      setEditStaffLoading(false);
    }
  };

  const handleToggleStaffStatus = async (email: string, currentStatus: string) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'Disabled' ? 'Active' : 'Disabled';
      await customersApi.toggleStaffStatus(email, newStatus);
      showSuccess(`Staff status updated to ${newStatus}`);
      await fetchStaffList();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to update staff status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (email: string) => {
    try {
      setLoading(true);
      await customersApi.cancelStaffInvitation(email);
      showSuccess(`Cancelled invitation for ${email}`);
      await fetchStaffList();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to cancel invitation');
    } finally {
      setLoading(false);
    }
  };

  // Billing State
  const [prepaidBalance, setPrepaidBalance] = useState<number>(0);
  const [creditBatches, setCreditBatches] = useState<CreditBatch[]>([]);
  const [usagePreference, setUsagePreference] = useState<string>('SUBSCRIPTION_FIRST');
  const [billingLoading, setBillingLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterPractice, setFilterPractice] = useState('');
  const [filterStaff, setFilterStaff] = useState('');

  // Role Permissions
  const isActiveStatus = userStatus?.onboarding_status === 'Active';
  const isSaaSAdmin = isActiveStatus && (userStatus?.is_owner_admin || userStatus?.is_customer_admin);

  // Auto-update session counters every 5 seconds
  useSessionPolling({ interval: 5000, enabled: !!activeSession });

  // Restore session on browser refresh
  useSessionResume();

  const handleCreateSession = async () => {
    setError('');
    setLoading(true);

    try {
      const session = await sessionsApi.createSession({
        customer_id: customerId,
        practice_id: practiceId,
        staff_id: user?.localAccountId || '',
        emr_connection_id: emrConnectionId,
        customer_staff_key: customerUserKey,
        timeout_minutes: 15,
        emr_username: emrUsername,
        emr_password: emrPassword,
      });

      // Pass transient credentials to the workflow state (not persisted)
      // Note: In a real app, these might be sent in the first processing request

      setActiveSession(session);
      setConnectionIds(customerId, practiceId, emrConnectionId);

      if (session.auto_closed_prior_session) {
        const priorDate = new Date(session.auto_closed_prior_session.started_at);
        const formatted = priorDate.toLocaleString();
        showWarning(`Prior session from ${formatted} was automatically closed.`);
      } else {
        showSuccess('Session created successfully!');
      }
      // Don't navigate immediately - user needs to upload PDFs first
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError(err.response.data.detail.message || 'Session conflict detected');
      } else {
        setError('Failed to create session. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreds = (practiceId: string) => {
    setSelectedPracticeId(practiceId);
    const practice = practices.find(p => p.practice_id === practiceId);
    setCreds({
      api_url: practice?.api_base_url || '',
      api_secret_key: practice?.api_customer_key || ''
    });
    setOpenCredsDialog(true);
  };

  const handleSaveCreds = async () => {
    if (!selectedPracticeId) return;
    try {
      setLoading(true);
      await customersApi.savePracticeCredentials(selectedPracticeId, creds);
      showSuccess('Credentials secured in Vault');
      setOpenCredsDialog(false);
    } catch (err) {
      showError('Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabIndex === 0) {
      fetchPractices();
    }
    if (tabIndex === 1) {
      fetchOrganizationSettings();
    }
    if (tabIndex === 2 && isSaaSAdmin) {
      fetchPractices();
    }
    if (tabIndex === 3 && isSaaSAdmin) {
      fetchStaffList();
    }
    if (tabIndex === 4 && isSaaSAdmin) {
      fetchBillingData();
      fetchSessionHistory();
      if (staffList.length === 0) fetchStaffList();
      if (practices.length === 0) fetchPractices();
    }
  }, [tabIndex, isSaaSAdmin]);

  const fetchOrganizationSettings = async () => {
    try {
      console.log('Fetching organization settings...');
      setLoading(true);
      const data = await customersApi.getOrganizationSettings();
      console.log('Organization settings fetched:', data);
      setOrganizationSettings({
        display_name: data.display_name || '',
        organization_name: data.organization_name || '',
        organization_website: data.organization_website || '',
        organization_support_email: data.organization_support_email || '',
        legal_name: data.legal_name || '',
        tax_id: data.tax_id || '',
        address_line1: data.address_line1 || '',
        address_line2: data.address_line2 || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        country: data.country || 'USA',
        timezone: data.timezone || 'America/Chicago',
        phone_country_code: data.phone_country_code || '+1',
        phone_number: data.phone_number || '',
        billing_email: data.billing_email || '',
        billing_channel: data.billing_channel || 'Stripe',
        organization_updated_at: data.organization_updated_at || '',
        is_owner_admin: userStatus?.is_owner_admin || false,
        is_customer_admin: userStatus?.is_customer_admin || false,
        is_staff: userStatus?.is_staff || false
      });
    } catch (err) {
      // If it's 404/403 just ignore, or maybe we want to show empty state
      console.log('Failed to fetch org settings', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganizationSettings = async () => {
    try {
      setLoading(true);
      // Construct payload mimicking the new DTO
      const payload = { ...organizationSettings };

      const res = await customersApi.updateOrganizationSettings(payload);
      showSuccess('Organization settings updated successfully');
      setOpenOrgDialog(false);

      // Update local state with returned values
      setOrganizationSettings(prev => ({
        ...prev,
        ...res
      }));
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to update organization settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionHistory = async (filters?: { start_date?: string; end_date?: string; practice_id?: string; staff_id?: string }) => {
    try {
      setSessionHistoryLoading(true);
      const data = await billingApi.getSessionHistory(filters);
      setSessionHistory(data);
    } catch (err) {
      console.error('Failed to fetch session history', err);
    } finally {
      setSessionHistoryLoading(false);
    }
  };

  const exportSessionHistoryCSV = () => {
    const headers = ['Date', 'Time (CST)', 'Practice', 'Staff', 'PDFs Uploaded', 'PDFs Processed', 'Status'];
    const rows = sessionHistory.map(row => [
      row.session_date,
      row.session_time_cst,
      `"${(row.practice_name || '').replace(/"/g, '""')}"`,
      `"${(row.staff_name || '').replace(/"/g, '""')}"`,
      row.pdfs_uploaded,
      row.pdfs_processed,
      row.session_status,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage_history_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchPractices = async () => {
    try {
      setLoading(true);
      const intent = sessionStorage.getItem('onboarding_intent');

      if (intent === 'enroll') {
        // Button 1 (Admin): require customerId before calling
        const effectiveCustomerId = customerId || (user as any)?.customer_id;
        if (!effectiveCustomerId) return;
        const data = await customersApi.listPractices(effectiveCustomerId);
        setPractices(data);
        if (data.length > 0 && !practiceId) {
          setPracticeId(data[0].practice_id);
        }
      } else {
        // Button 2/3 (Staff): backend derives customer from JWT
        const data = await customersApi.listPractices('');
        setPractices(data);
        if (data.length > 0 && !practiceId) {
          setPracticeId(data[0].practice_id);
        }
      }
    } catch (err) {
      showError('Failed to fetch practices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const data = await customersApi.listStaff();
      setStaffList(data);
    } catch (err) {
      showError('Failed to fetch staff list');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePractice = async () => {
    try {
      setLoading(true);
      if (editingPracticeId) {
        await customersApi.updatePractice(editingPracticeId, newPractice as any);
        showSuccess('Practice updated successfully!');
      } else {
        await customersApi.addPractice({
          customer_id: customerId,
          ...newPractice
        } as any);
        showSuccess('Practice added successfully!');
      }
      setOpenPracticeDialog(false);
      setEditingPracticeId(null);
      await fetchPractices();
      // Reset form
      setNewPractice({
        global_name: '',
        external_code: '',
        api_base_url: '',
        api_customer_key: '',
        emr_vendor: 'Tebra',
        city: '',
        state: '',
      });
    } catch (err) {
      showError('Failed to save practice');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateAccount = async () => {
    try {
      setActivating(true);
      await onboardingApi.activate();
      await refreshStatus();
      showSuccess('Account activated! Welcome to FaceSheetApp.');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Activation failed';
      showError(detail);
    } finally {
      setActivating(false);
    }
  };

  const handleEditPractice = (practice: any) => {
    setEditingPracticeId(practice.practice_id);
    setNewPractice({
      global_name: practice.global_name,
      external_code: practice.external_code || '',
      api_base_url: practice.api_base_url || '',
      api_customer_key: practice.api_customer_key || '',
      emr_vendor: practice.emr_vendor || 'Tebra',
      city: practice.city,
      state: practice.state,
    });
    setOpenPracticeDialog(true);
  };

  const handleDeletePractice = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this practice? This will also delete all associated sessions.')) return;
    try {
      setLoading(true);
      await customersApi.deletePractice(id);
      showSuccess('Practice deleted');
      fetchPractices();
    } catch (err) {
      showError('Failed to delete practice');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      const [balanceRes, prefRes] = await Promise.all([
        billingApi.getPrepaidBalance(),
        billingApi.getUsagePreference()
      ]);
      setPrepaidBalance(balanceRes.total_credits);
      setCreditBatches(balanceRes.batches);
      setUsagePreference(prefRes.preference);
    } catch (err) {
      console.error('Failed to fetch billing data', err);
    } finally {
      setBillingLoading(false);
    }
  };

  const handlePreferenceChange = async (newPref: string) => {
    try {
      await billingApi.updateUsagePreference(newPref);
      setUsagePreference(newPref);
      showSuccess('Usage preference updated');
    } catch (err) {
      showError('Failed to update preference');
    }
  };



  const handleSubscribeClick = () => {
    setOpenPlanDialog(true);
  };

  const handlePlanSelected = async (plan: SubscriptionPlan) => {
    setOpenPlanDialog(false);
    try {
      setLoading(true);
      const priceId = plan.priceId;

      if (!priceId) {
        alert(`System Config Error: Price ID for ${plan.name} is missing. Please check your .env file.`);
        return;
      }

      const { checkout_url } = await onboardingApi.createCheckoutSession(
        priceId,
        window.location.origin + '/dashboard?subscription=success',
        window.location.origin + '/dashboard?subscription=cancel'
      );
      window.location.href = checkout_url;
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showError(detail || "Failed to initiate subscription checkout.");
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      const { url } = await billingApi.createPortalSession({
        return_url: window.location.origin + '/dashboard'
      });
      window.location.href = url;
    } catch (err: any) {
      if (err.response?.status === 400) {
        if (window.confirm("No active subscription found. Would you like to subscribe to the FaceSheetApp Plan now?")) {
          handleSubscribeClick();
          return;
        }
      }
      showError('Failed to load billing portal. Ensure you have an active billing account.');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async () => {
    try {
      setLoading(true);
      const { checkout_url } = await billingApi.createBulkCheckout(
        window.location.origin + '/subscription-success',
        window.location.origin + '/dashboard'
      );
      window.location.href = checkout_url;
    } catch (err) {
      showError('Failed to initiate bulk check-out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            FaceSheetApp
          </Typography>
          <Typography variant="body1" sx={{ mr: 3 }}>
            {user?.name || user?.username}
          </Typography>
          {prepaidBalance > 0 && (
            <Box sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              px: 2, py: 0.5, borderRadius: 1, mr: 2,
              display: 'flex', alignItems: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Credits: {prepaidBalance}
              </Typography>
            </Box>
          )}
          <Button color="inherit" onClick={logout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.name || 'User'}
        </Typography>

        {isActiveStatus && isSaaSAdmin ? (
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            textColor="primary"
            indicatorColor="secondary"
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 500 },
              '& .Mui-selected': { color: 'primary.main', fontWeight: 700 },
            }}
          >
            <Tab label="Process PDFs" />
            <Tab label="Manage Customer/Organization" />
            <Tab label="Practices" />
            <Tab label="Provision Staff" />
            <Tab label="Billing & Credits" />
          </Tabs>
        ) : isActiveStatus ? (
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            textColor="primary"
            indicatorColor="secondary"
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 500 },
              '& .Mui-selected': { color: 'primary.main', fontWeight: 700 },
            }}
          >
            <Tab label="Process PDFs" />
          </Tabs>
        ) : null}

        {tabIndex === 0 && (
          <Grid container spacing={4}>
            {/* Create Session Card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    EMR Connection & Session Linkage
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Select Practice Location</InputLabel>
                        <Select
                          value={practiceId}
                          onChange={(e) => {
                            const pid = e.target.value as string;
                            setPracticeId(pid);
                            // Reset transient credentials on practice change
                            setEmrUsername('');
                            setEmrPassword('');
                          }}
                          label="Select Practice Location"
                        >
                          {practices.length === 0 ? (
                            <MenuItem disabled value="">
                              <em>No authorized practices found</em>
                            </MenuItem>
                          ) : (
                            practices.map((p) => (
                              <MenuItem key={p.practice_id} value={p.practice_id}>
                                {p.global_name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>

                      {practiceId && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Practice Configuration Summary
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid size={6}>
                              <Typography variant="caption" color="text.secondary" display="block">Legal Name</Typography>
                              <Typography variant="body2">{practices.find(p => p.practice_id === practiceId)?.global_name}</Typography>
                            </Grid>
                            <Grid size={6}>
                              <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
                              <Typography variant="body2">
                                {practices.find(p => p.practice_id === practiceId)?.city}, {practices.find(p => p.practice_id === practiceId)?.state}
                              </Typography>
                            </Grid>
                            <Grid size={12}>
                              <Typography variant="caption" color="text.secondary" display="block">Provisioned API URL</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {practices.find(p => p.practice_id === practiceId)?.api_base_url || 'https://api.tebra.com/v1'}
                              </Typography>
                            </Grid>
                            <Grid size={12}>
                              <Typography variant="caption" color="text.secondary" display="block">Customer Key (Secured)</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                ••••••••{practices.find(p => p.practice_id === practiceId)?.api_customer_key?.slice(-4) || '••••'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Grid>

                    <Grid size={12}>
                      <TextField
                        label="Billing & Provisioning Key"
                        fullWidth
                        value={customerUserKey}
                        onChange={(e) => setCustomerUserKey(e.target.value)}
                        placeholder="Alpha-numeric billing identifier"
                        variant="outlined"
                        slotProps={{
                          input: {
                            readOnly: !!userStatus?.customer_staff_key,
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        Linked to your organization's billing plan
                      </Typography>
                    </Grid>

                    <Grid size={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Transient User Authentication
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                        <Typography variant="caption">
                          Your EMR username and password are not stored on our servers. They are used only for the duration of this session (HIPAA Compliant).
                        </Typography>
                      </Alert>
                    </Grid>

                    <Grid size={6}>
                      <TextField
                        label="EMR Username"
                        fullWidth
                        autoComplete="username"
                        value={emrUsername}
                        onChange={(e) => setEmrUsername(e.target.value)}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="EMR Password"
                        type="password"
                        fullWidth
                        autoComplete="current-password"
                        value={emrPassword}
                        onChange={(e) => setEmrPassword(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
                    onClick={handleCreateSession}
                    disabled={!practiceId || !customerUserKey || !emrUsername || !emrPassword || loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Establish Secure Connection'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Active Session Card */}
            {activeSession && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Active Session
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Session ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {activeSession.session_id}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1">{activeSession.session_status}</Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total PDFs
                        </Typography>
                        <Typography variant="h6">
                          {activeSession.counters.total_pdfs}
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">
                          Processed
                        </Typography>
                        <Typography variant="h6">
                          {activeSession.counters.pdfs_processed_count}
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">
                          New Records
                        </Typography>
                        <Typography variant="h6">
                          {activeSession.counters.new_records_count}
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">
                          Updated
                        </Typography>
                        <Typography variant="h6">
                          {activeSession.counters.updated_records_count}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => navigate('/workflow')}
                    >
                      Go to Workflow
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Folder Upload - Only shown when session is active */}
            {activeSession && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FolderUpload />
              </Grid>
            )}
          </Grid>
        )}
        {tabIndex === 1 && (
          <Box>
            <Paper sx={{ p: 4, mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" color="primary">Manage Customer/Organization</Typography>
                {isSaaSAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setOpenOrgDialog(true)}
                  >
                    Edit Information
                  </Button>
                )}
              </Box>

              <Grid container spacing={4}>
                {/* Block 1: Organization Profile */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
                    Organization Profile (Public/Support Identity)
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Display Name</Typography>
                    <Typography variant="body1">{organizationSettings.display_name || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Organization Name</Typography>
                    <Typography variant="body1">{organizationSettings.organization_name || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Website</Typography>
                    <Typography variant="body1">{organizationSettings.organization_website || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Support Email</Typography>
                    <Typography variant="body1">{organizationSettings.organization_support_email || 'Not set'}</Typography>
                  </Box>
                </Grid>

                {/* Block 2: Legal & Corporate Identity */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
                    Legal & Corporate Identity
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Legal Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{organizationSettings.legal_name || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Tax ID / EIN</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{organizationSettings.tax_id || 'Not set'}</Typography>
                  </Box>
                </Grid>

                {/* Block 3: Contact & Location Information */}
                <Grid size={12}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 2 }}>
                    Contact & Location Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" display="block">Address</Typography>
                        <Typography variant="body1">
                          {organizationSettings.address_line1} {organizationSettings.address_line2 && `, ${organizationSettings.address_line2}`}
                          <br />
                          {organizationSettings.city && `${organizationSettings.city},`} {organizationSettings.state} {organizationSettings.zip_code}
                          <br />
                          {organizationSettings.country}
                        </Typography>
                        {!organizationSettings.address_line1 && <Typography variant="body2" color="textSecondary">Not set</Typography>}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" display="block">Primary Phone</Typography>
                        <Typography variant="body1">
                          {organizationSettings.phone_country_code} {organizationSettings.phone_number || 'Not set'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary" display="block">Timezone</Typography>
                        <Typography variant="body1">{organizationSettings.timezone || 'Not set'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Block 4: Billing & Subscription Configuration */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 2 }}>
                    Billing & Subscription Configuration
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Billing Email</Typography>
                    <Typography variant="body1">{organizationSettings.billing_email || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Billing Channel</Typography>
                    <Typography variant="body1">{(userStatus as any)?.billing_channel || 'Not set'}</Typography>
                  </Box>
                </Grid>

                {/* Block 5: System & Account Status */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 2 }}>
                    System & Account Status
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Enrollment Status</Typography>
                    <Typography variant="body1" color={(userStatus as any)?.is_active ? 'success.main' : 'error.main'}>
                      {userStatus?.onboarding_status}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Customer ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {customerId}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block">Last Updated Settings</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {organizationSettings.organization_updated_at ? new Date(organizationSettings.organization_updated_at).toLocaleString() : 'Never'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Roles Block */}
                <Grid size={12}>
                  <Typography variant="h6" sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 2 }}>
                    Your Scoped Roles
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <FormControlLabel
                      control={<Checkbox checked={organizationSettings.is_owner_admin} disabled />}
                      label="Owner Admin"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={organizationSettings.is_customer_admin} disabled />}
                      label="Customer Admin"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={organizationSettings.is_staff} disabled />}
                      label="Staff"
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Roles are verified via Entra ID and your organizational links. Only Administrators can change roles during editing.
                  </Typography>
                </Grid>

              </Grid>
            </Paper>

            {/* Edit Dialog */}
            <Dialog open={openOrgDialog} onClose={() => setOpenOrgDialog(false)} fullWidth maxWidth="md">
              <DialogTitle>Edit Customer / Organization Settings</DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Typography color="primary" variant="subtitle2">Legal & Corporate Identity</Typography>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Legal Name" size="small"
                      value={organizationSettings.legal_name}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, legal_name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Tax ID / EIN" size="small"
                      value={organizationSettings.tax_id}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, tax_id: e.target.value })}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography color="primary" variant="subtitle2" sx={{ mt: 2 }}>Organization Profile (Public Identity)</Typography>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Display Name" size="small"
                      value={organizationSettings.display_name}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, display_name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Organization Name" size="small"
                      value={organizationSettings.organization_name}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, organization_name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Website URL" size="small"
                      value={organizationSettings.organization_website}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, organization_website: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Support Email" size="small"
                      value={organizationSettings.organization_support_email}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, organization_support_email: e.target.value })}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography color="primary" variant="subtitle2" sx={{ mt: 2 }}>Contact & Location Information</Typography>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Address Line 1" size="small"
                      value={organizationSettings.address_line1}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, address_line1: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Address Line 2" size="small"
                      value={organizationSettings.address_line2}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, address_line2: e.target.value })}
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth label="City" size="small"
                      value={organizationSettings.city}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, city: e.target.value })}
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth label="State" size="small"
                      value={organizationSettings.state}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, state: e.target.value })}
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth label="Zip Code" size="small"
                      value={organizationSettings.zip_code}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, zip_code: e.target.value })}
                    />
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      fullWidth label="Country Code" size="small"
                      value={organizationSettings.phone_country_code}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, phone_country_code: e.target.value })}
                    />
                  </Grid>
                  <Grid size={5}>
                    <TextField
                      fullWidth label="Phone Number" size="small"
                      value={organizationSettings.phone_number}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, phone_number: e.target.value })}
                    />
                  </Grid>
                  <Grid size={5}>
                    <TextField
                      fullWidth label="Country" size="small"
                      value={organizationSettings.country}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, country: e.target.value })}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography color="primary" variant="subtitle2" sx={{ mt: 2 }}>Billing Configuration</Typography>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth label="Billing Email" size="small" type="email"
                      value={organizationSettings.billing_email}
                      onChange={(e) => setOrganizationSettings({ ...organizationSettings, billing_email: e.target.value })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Billing Channel</InputLabel>
                      <Select
                        value={organizationSettings.billing_channel || 'Stripe'}
                        label="Billing Channel"
                        onChange={(e) => setOrganizationSettings({ ...organizationSettings, billing_channel: e.target.value })}
                      >
                        <MenuItem value="Stripe">Stripe</MenuItem>
                        <MenuItem value="Azure">Azure</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {userStatus?.is_owner_admin && (
                    <Grid size={12}>
                      <Typography color="primary" variant="subtitle2" sx={{ mt: 2 }}>Role Assignment (Admin Override)</Typography>
                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <FormControlLabel
                          control={<Checkbox checked={organizationSettings.is_owner_admin} onChange={(e: any) => setOrganizationSettings({ ...organizationSettings, is_owner_admin: e.target.checked })} />}
                          label="Owner Admin"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={organizationSettings.is_customer_admin} onChange={(e: any) => setOrganizationSettings({ ...organizationSettings, is_customer_admin: e.target.checked })} />}
                          label="Customer Admin"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={organizationSettings.is_staff} onChange={(e: any) => setOrganizationSettings({ ...organizationSettings, is_staff: e.target.checked })} />}
                          label="Staff"
                        />
                      </Box>
                    </Grid>
                  )}

                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenOrgDialog(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveOrganizationSettings} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {tabIndex === 2 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>Manage Practices</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Add practices and configure their EMR API settings.
            </Typography>
            <Button
              variant="contained"
              sx={{ mb: 2 }}
              onClick={() => {
                setEditingPracticeId(null);
                setNewPractice({
                  global_name: '',
                  external_code: '',
                  api_base_url: '',
                  api_customer_key: '',
                  emr_vendor: 'Tebra',
                  city: '',
                  state: '',
                });
                setOpenPracticeDialog(true);
              }}
            >
              Add Practice
            </Button>

            {practices.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {practices.map((p) => (
                  <Paper key={p.practice_id} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{p.global_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {p.city}, {p.state} | Vendor: {p.emr_vendor}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Code: {p.external_code || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => handleOpenCreds(p.practice_id)}>Credentials</Button>
                      <Button size="small" onClick={() => handleEditPractice(p)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDeletePractice(p.practice_id)}>Delete</Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Alert severity="warning">No practices configured. Add your first practice client to begin.</Alert>
            )}

            <Dialog open={openPracticeDialog} onClose={() => setOpenPracticeDialog(false)} fullWidth maxWidth="sm">
              <DialogTitle>{editingPracticeId ? 'Edit Practice' : 'Add New Practice'}</DialogTitle>
              <DialogContent>
                <Box component="form" sx={{ mt: 1 }}>
                  <TextField
                    fullWidth label="Practice Name"
                    margin="normal" value={newPractice.global_name}
                    onChange={(e) => setNewPractice({ ...newPractice, global_name: e.target.value })}
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>EMR Vendor</InputLabel>
                    <Select
                      value={newPractice.emr_vendor}
                      label="EMR Vendor"
                      onChange={(e) => setNewPractice({ ...newPractice, emr_vendor: e.target.value })}
                    >
                      <MenuItem value="Tebra">Tebra</MenuItem>
                      {/* More vendors can be added here in the future */}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth label="External Code (NPI/ID)"
                    margin="normal" value={newPractice.external_code}
                    onChange={(e) => setNewPractice({ ...newPractice, external_code: e.target.value })}
                  />
                  <TextField
                    fullWidth label="EMR API Base URL"
                    margin="normal" value={newPractice.api_base_url}
                    onChange={(e) => setNewPractice({ ...newPractice, api_base_url: e.target.value })}
                    placeholder="https://api.tebra.com/v1"
                  />
                  <TextField
                    fullWidth label="EMR Customer Key"
                    margin="normal" value={newPractice.api_customer_key}
                    onChange={(e) => setNewPractice({ ...newPractice, api_customer_key: e.target.value })}
                  />
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <TextField
                        fullWidth label="City"
                        margin="normal" value={newPractice.city}
                        onChange={(e) => setNewPractice({ ...newPractice, city: e.target.value })}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth label="State"
                        margin="normal" value={newPractice.state}
                        onChange={(e) => setNewPractice({ ...newPractice, state: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenPracticeDialog(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSavePractice} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Practice'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* DEBUG: Reset Onboarding Button */}
            <Box sx={{ mt: 8, p: 2, border: '1px dashed red', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="error" display="block" gutterBottom>
                ⚠ DEBUG ZONE: Use this if you are stuck in a loop or need to re-test onboarding.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={async () => {
                  if (!window.confirm('Reset your onboarding status to "Authenticated"? This will force you back to the wizard.')) return;
                  try {
                    await onboardingApi.updateProfile({ onboarding_status: 'Authenticated' } as any);
                    window.location.reload();
                  } catch (e) {
                    alert('Failed to reset: ' + JSON.stringify(e));
                  }
                }}
              >
                Reset Onboarding Status
              </Button>
            </Box>

            {/* Credentials Dialog (Admin Setup) */}
            <Dialog open={openCredsDialog} onClose={() => setOpenCredsDialog(false)} fullWidth maxWidth="xs">
              <DialogTitle>Secure EMR API Setup</DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Specify the production API endpoint and Secret Key (e.g. Tebra Customer Key).
                    These are practice-wide constants stored in Azure Key Vault.
                  </Typography>
                  <TextField
                    fullWidth label="EMR API Base URL"
                    margin="normal" value={creds.api_url}
                    onChange={(e) => setCreds({ ...creds, api_url: e.target.value })}
                    placeholder="https://api.tebra.com/v1"
                  />
                  <TextField
                    fullWidth label="API Secret Key"
                    margin="normal" value={creds.api_secret_key}
                    onChange={(e) => setCreds({ ...creds, api_secret_key: e.target.value })}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenCredsDialog(false)}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleSaveCreds} disabled={loading || !creds.api_url || !creds.api_secret_key}>
                  {loading ? 'Securing...' : 'Encrypt & Save Config'}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}

        {tabIndex === 3 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>Provision Staff</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Invite employees and assign their customer_staff_key.
            </Typography>

            {provisionResult && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Success!</Typography>
                <Typography variant="body2">{provisionResult.message}</Typography>
                {provisionResult.data?.customer_staff_key && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed grey' }}>
                    <Typography variant="caption" display="block">Provisioned Key (Share this with staff):</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {provisionResult.data.customer_staff_key}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            <Box component="form" sx={{ maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Staff Email"
                margin="dense"
                value={provisionEmail}
                onChange={(e) => setProvisionEmail(e.target.value)}
              />
              <TextField
                fullWidth
                label="Custom Key (Optional)"
                margin="dense"
                placeholder="Auto-generated if empty"
                value={provisionKey}
                onChange={(e) => setProvisionKey(e.target.value)}
                helperText="Leave blank to auto-generate (Recommended)"
              />
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Assign to Practices</Typography>
                <Grid container spacing={2}>
                  {practices.map((p) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={p.practice_id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={provisionPracticeIds.includes(p.practice_id)}
                            onChange={(e) => {
                              if (e.target.checked) setProvisionPracticeIds([...provisionPracticeIds, p.practice_id]);
                              else setProvisionPracticeIds(provisionPracticeIds.filter(id => id !== p.practice_id));
                            }}
                          />
                        }
                        label={p.global_name}
                      />
                    </Grid>
                  ))}
                  {practices.length === 0 && <Typography variant="caption" color="text.secondary">No practices available.</Typography>}
                </Grid>
              </Box>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Staff Roles</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={provisionRoles.isStaff}
                          onChange={(e) => setProvisionRoles({ ...provisionRoles, isStaff: e.target.checked })}
                        />
                      }
                      label="Staff"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={provisionRoles.isCustomerAdmin}
                          onChange={(e) => setProvisionRoles({ ...provisionRoles, isCustomerAdmin: e.target.checked })}
                        />
                      }
                      label="Customer Admin"
                    />
                  </Grid>
                  {isSaaSAdmin && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={provisionRoles.isOwnerAdmin}
                            onChange={(e) => setProvisionRoles({ ...provisionRoles, isOwnerAdmin: e.target.checked })}
                          />
                        }
                        label="Owner Admin"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleProvision}
                disabled={loading || !provisionEmail}
              >
                {loading ? 'Provisioning...' : 'Provision & Send Invite'}
              </Button>
            </Box>

            {staffList.length > 0 && (
              <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" gutterBottom>Provisioned Staff</Typography>
                <Paper variant="outlined" sx={{ mt: 2 }}>
                  {staffList.map((member, idx) => (
                    <Box key={member.staff_id || `invite-${idx}`} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="subtitle2">{member.email}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(member.first_name || member.last_name)
                              ? `${(member.first_name || '')} ${(member.last_name || '')}`.trim() + ` (${member.status})`
                              : `(${member.status})`}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" display="block">Key</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {member.customer_staff_key}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" display="block">Role</Typography>
                          <Typography variant="body2">{member.role}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                          <Typography variant="caption" display="block">Status</Typography>
                          <Typography variant="body2" color={member.status === 'Active' ? 'success.main' : member.status === 'Disabled' ? 'error.main' : 'text.secondary'}>
                            {member.status}
                          </Typography>
                          {(isSaaSAdmin || userStatus?.staff_id !== member.staff_id) && member.status !== 'Disabled' && member.status !== 'Active' && member.status !== 'Invited' && member.status !== 'Pending' ? null : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ p: 0.5, fontSize: '0.7rem' }}
                                onClick={() => handleOpenEditStaff(member)}
                                title="Edit Roles & Practices"
                              >
                                Edit
                              </Button>
                              {member.status === 'Pending' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  sx={{ p: 0.5, fontSize: '0.7rem' }}
                                  onClick={() => handleCancelInvitation(member.email)}
                                  title="Cancel Invitation"
                                >
                                  Cancel Invite
                                </Button>
                              )}
                              {member.status !== 'Pending' && member.status !== 'Invited' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={member.status === 'Disabled' ? 'primary' : 'error'}
                                  sx={{ p: 0.5, fontSize: '0.7rem' }}
                                  onClick={() => handleToggleStaffStatus(member.email, member.status)}
                                  title={member.status === 'Disabled' ? 'Enable Staff Access' : 'Disable Staff Access'}
                                >
                                  {member.status === 'Disabled' ? 'Enable' : 'Disable'}
                                </Button>
                              )}
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                      {member.practice_ids && member.practice_ids.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary">Practices: </Typography>
                          <Typography variant="caption">
                            {member.practice_ids
                              .map((pid: string) => practices.find(p => p.practice_id === pid)?.global_name || pid)
                              .join(', ')}
                          </Typography>
                        </Grid>
                      )}
                    </Box>
                  ))}
                </Paper>
              </>
            )}

            {/* Edit Staff Modal */}
            <Dialog open={editStaffModalOpen} onClose={() => setEditStaffModalOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Edit Staff Assignments</DialogTitle>
              <DialogContent dividers>
                {editingStaff && (
                  <Box>
                    <Typography variant="subtitle2">Staff Member:</Typography>
                    <Typography variant="body2" gutterBottom>{editingStaff.email}</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Roles</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControlLabel
                          control={<Checkbox checked={editStaffRoles.isStaff} onChange={e => setEditStaffRoles({ ...editStaffRoles, isStaff: e.target.checked })} />}
                          label="Staff"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControlLabel
                          control={<Checkbox checked={editStaffRoles.isCustomerAdmin} onChange={e => setEditStaffRoles({ ...editStaffRoles, isCustomerAdmin: e.target.checked })} />}
                          label="Customer Admin"
                        />
                      </Grid>
                      {isSaaSAdmin && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={<Checkbox checked={editStaffRoles.isOwnerAdmin} onChange={e => setEditStaffRoles({ ...editStaffRoles, isOwnerAdmin: e.target.checked })} />}
                            label="Owner Admin"
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Assigned Practices</Typography>
                    <Grid container spacing={2}>
                      {practices.map((p) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={p.practice_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editStaffPracticeIds.includes(p.practice_id)}
                                onChange={(e) => {
                                  if (e.target.checked) setEditStaffPracticeIds([...editStaffPracticeIds, p.practice_id]);
                                  else setEditStaffPracticeIds(editStaffPracticeIds.filter(id => id !== p.practice_id));
                                }}
                              />
                            }
                            label={p.global_name}
                          />
                        </Grid>
                      ))}
                      {practices.length === 0 && <Typography variant="caption" color="text.secondary">No practices available.</Typography>}
                    </Grid>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditStaffModalOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveStaffEdits} disabled={editStaffLoading}>
                  {editStaffLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogActions>
            </Dialog>

          </Paper>
        )}

        {tabIndex === 4 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 700 }}>Billing & Credits</Typography>

            {/* ── Section 1: Usage History ── */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 1 }}>Usage History</Typography>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Practice</InputLabel>
                  <Select
                    value={filterPractice}
                    label="Practice"
                    onChange={(e) => setFilterPractice(e.target.value)}
                  >
                    <MenuItem value="">All Practices</MenuItem>
                    {practices.map((p) => (
                      <MenuItem key={p.practice_id} value={p.practice_id}>{p.global_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Staff</InputLabel>
                  <Select
                    value={filterStaff}
                    label="Staff"
                    onChange={(e) => setFilterStaff(e.target.value)}
                  >
                    <MenuItem value="">All Staff</MenuItem>
                    {staffList.map((s) => (
                      <MenuItem key={s.staff_id} value={s.staff_id}>
                        {(s.first_name || s.last_name) ? `${s.first_name || ''} ${s.last_name || ''}`.trim() : s.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => fetchSessionHistory({
                  start_date: filterStartDate || undefined,
                  end_date: filterEndDate || undefined,
                  practice_id: filterPractice || undefined,
                  staff_id: filterStaff || undefined,
                })}
                disabled={sessionHistoryLoading}
              >
                {sessionHistoryLoading ? 'Loading...' : 'Apply Filters'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilterStartDate('');
                  setFilterEndDate('');
                  setFilterPractice('');
                  setFilterStaff('');
                  fetchSessionHistory();
                }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={exportSessionHistoryCSV}
                disabled={sessionHistory.length === 0}
              >
                Export CSV
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380, overflowY: 'auto', mb: 4 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>Time (CST)</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>Practice</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>Staff</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }} align="right">PDFs Uploaded</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }} align="right">PDFs Processed</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionHistoryLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : sessionHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No sessions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessionHistory.map((row) => (
                      <TableRow key={row.session_id} hover>
                        <TableCell>{row.session_date}</TableCell>
                        <TableCell>{row.session_time_cst}</TableCell>
                        <TableCell>{row.practice_name}</TableCell>
                        <TableCell>{row.staff_name}</TableCell>
                        <TableCell align="right">{row.pdfs_uploaded}</TableCell>
                        <TableCell align="right">{row.pdfs_processed}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.session_status}
                            size="small"
                            color={row.session_status === 'Completed' ? 'success' : row.session_status === 'Active' ? 'primary' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            {/* ── Section 2: Subscribe to a Plan ── */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ mb: 3, bgcolor: 'success.light', borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Subscribe to a Plan</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Choose a subscription plan that fits your practice needs. Start with our free test plan or select a paid tier.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      onClick={() => setOpenLocationConfirmDialog(true)}
                      disabled={loading}
                    >
                      Subscribe Now
                    </Button>
                  </CardContent>
                </Card>

                {/* ── Section 3: Manage Subscription & Payments ── */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Manage Subscription & Payments</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Update payment methods, download invoices, or change your plan.
                    </Typography>
                    <Button variant="outlined" fullWidth onClick={handleManageSubscription} disabled={loading}>
                      Manage Subscription & Payments
                    </Button>
                  </CardContent>
                </Card>

                {userStatus?.onboarding_status !== 'Active' && (
                  <Card variant="outlined" sx={{ mb: 3, borderColor: 'warning.main', bgcolor: '#fff8e1' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                        Account Not Yet Active
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        If you have already completed your Stripe payment but your account still shows as inactive, click below to sync your activation status.
                      </Typography>
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        onClick={handleActivateAccount}
                        disabled={activating}
                      >
                        {activating ? 'Activating...' : 'Check & Activate Account'}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {/* ── Section 4: Purchase Bulk Credits ── */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Purchase Bulk Credits</Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Add a batch of 2,000 PDFs for a flat $1,000 fee ($0.50/PDF).
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      color="secondary"
                      onClick={handleBuyCredits}
                      disabled={loading}
                      sx={{ mb: 2 }}
                    >
                      Buy 2,000 PDF Package
                    </Button>

                    <Card variant="outlined" sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>Total Prepaid Credits</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{prepaidBalance} PDFs</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>Valid for 1 year from purchase. Non-refundable.</Typography>
                      </CardContent>
                    </Card>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Active Credit Batches</Typography>
                    {(creditBatches || []).length > 0 ? (
                      <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                        {(creditBatches || []).map((batch) => (
                          <Paper key={batch.credit_id} variant="outlined" sx={{ p: 1, mb: 1 }}>
                            <Grid container justifyContent="space-between">
                              <Typography variant="body2"><strong>{batch.pdfs_remaining}</strong> PDFs left</Typography>
                              <Typography variant="caption">Expires: {new Date(batch.expires_at_utc).toLocaleDateString()}</Typography>
                            </Grid>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No active credit batches</Typography>
                    )}
                  </CardContent>
                </Card>

                {/* ── Section 5: Billing Priority ── */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Billing Priority</Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Choose how you want to be billed when multiple options exist.
                    </Alert>
                    <FormControl fullWidth>
                      <InputLabel>Billing Priority</InputLabel>
                      <Select
                        value={usagePreference}
                        label="Billing Priority"
                        onChange={(e) => handlePreferenceChange(e.target.value)}
                        disabled={billingLoading}
                      >
                        <MenuItem value="SUBSCRIPTION_FIRST">
                          <strong>Subscription First</strong>&nbsp;(Use monthly inclusions, then credits)
                        </MenuItem>
                        <MenuItem value="CREDITS_FIRST">
                          <strong>Credits First</strong>&nbsp;(Exhaust prepaid credits before using subscription)
                        </MenuItem>
                        <MenuItem value="IGNORE_CREDITS">
                          <strong>Ignore Credits</strong>&nbsp;(Always bill to monthly subscription)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Container>

      {/* US Location Confirmation Dialog */}
      <Dialog
        open={openLocationConfirmDialog}
        onClose={() => setOpenLocationConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Your Location</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Our subscription services are exclusively available to healthcare providers located in the United States.
          </Typography>
          <Typography variant="body1" paragraph>
            By continuing, you confirm that:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">You are a healthcare provider operating in the United States</Typography>
            </li>
            <li>
              <Typography variant="body2">Your billing address is located in the United States</Typography>
            </li>
            <li>
              <Typography variant="body2">You understand that services are restricted to US-based customers only</Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLocationConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log('[COMPLIANCE] User acknowledged US-only service disclaimer', {
                timestamp: new Date().toISOString(),
                customerId: customerId,
                email: user?.username
              });
              setOpenLocationConfirmDialog(false);
              setOpenPlanDialog(true);
            }}
            variant="contained"
            color="primary"
          >
            I Confirm - Continue to Plans
          </Button>
        </DialogActions>
      </Dialog>

      <PlanSelectionDialog
        open={openPlanDialog}
        onClose={() => setOpenPlanDialog(false)}
        onSelectPlan={handlePlanSelected}
      />


    </Box >
  );
};
